
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';

// Eager imports — load-bearing for first paint / LCP / SEO. The three
// public deal-engine landing variants render immediately on /, /buyer,
// /broker so any lazy-loading delay there hurts crawler indexing and Core
// Web Vitals.
import LandingColorSeller from '@/pages/preview/LandingColorSeller';
import LandingColorBuyer from '@/pages/preview/LandingColorBuyer';
import LandingColorEfficiency from '@/pages/preview/LandingColorEfficiency';
// /scan is a cold-traffic lead-magnet page (paid + Reddit). LCP critical → eager.
import LandingScan from '@/pages/LandingScan';

// Lazy imports — every other route. Each lands in its own chunk so blog
// visitors don't pay for Supabase + pdf.js + Summary code, and authed users
// don't pay for the full preview/walkthrough surface. A single Suspense
// boundary wraps <Routes> below so any lazy load shows the BrandLoader
// fallback while the chunk fetches.
const Auth = lazy(() => import('@/pages/Auth'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Search = lazy(() => import('@/pages/Search'));
const Summary = lazy(() => import('@/pages/Summary'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const UpdatePassword = lazy(() => import('@/pages/UpdatePassword'));
const NewHouse = lazy(() => import('@/pages/NewHouse'));
const Upgrade = lazy(() => import('@/pages/Upgrade'));
const UpgradeLite = lazy(() => import('@/pages/preview/UpgradeLite'));
const CheckoutSuccess = lazy(() => import('@/pages/CheckoutSuccess'));
const Subscription = lazy(() => import('@/pages/Subscription'));
const Settings = lazy(() => import('@/pages/Settings'));
const Docs = lazy(() => import('@/pages/Docs'));
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const AgentInvite = lazy(() => import('@/pages/AgentInvite'));
const PublicListing = lazy(() => import('@/pages/PublicListing'));
const OfferComparisonShare = lazy(() => import('@/pages/OfferComparisonShare'));
const Listings = lazy(() => import('@/pages/Listings'));
const Chat = lazy(() => import('@/pages/Chat'));
const Privacy = lazy(() => import('@/pages/legal/Privacy'));
const Terms = lazy(() => import('@/pages/legal/Terms'));
const PreviewB = lazy(() => import('@/pages/preview/PreviewB'));
const PreviewC = lazy(() => import('@/pages/preview/PreviewC'));
const PreviewCHome = lazy(() => import('@/pages/preview/PreviewCHome'));
const PreviewCAddProperty = lazy(() => import('@/pages/preview/PreviewCAddProperty'));
const PreviewCSettings = lazy(() => import('@/pages/preview/PreviewCSettings'));
const OfferComparisonPreview = lazy(() => import('@/pages/preview/OfferComparisonPreview'));
const LandingIndex = lazy(() => import('@/pages/preview/LandingIndex'));
const LandingSeller = lazy(() => import('@/pages/preview/LandingSeller'));
const LandingBuyer = lazy(() => import('@/pages/preview/LandingBuyer'));
const LandingEfficiency = lazy(() => import('@/pages/preview/LandingEfficiency'));
const HomeBuyerFirst = lazy(() => import('@/pages/preview/HomeBuyerFirst'));
const HomeBuyerFamily = lazy(() => import('@/pages/preview/HomeBuyerFamily'));
const HomeBuyerSavvy = lazy(() => import('@/pages/preview/HomeBuyerSavvy'));
const Walkthrough = lazy(() => import('@/pages/preview/Walkthrough'));
const BlogIndex = lazy(() => import('@/pages/preview/blog/BlogIndex'));
const BlogArticle = lazy(() => import('@/pages/preview/blog/BlogArticle'));
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'));
const AdminAllProperties = lazy(() => import('@/pages/admin/AllProperties'));
const AdminPropertyDashboard = lazy(() => import('@/pages/admin/PropertyAdminDashboard'));
const AdminPlaceholder = lazy(() => import('@/pages/admin/Placeholder'));
const AdminAuditLog = lazy(() => import('@/pages/admin/AuditLog'));

import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Toaster } from '@/components/ui/sonner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useQuota } from '@/hooks/useQuota';
import { useReferralCapture } from '@/hooks/useReferralCapture';
import { useDevLoginAs } from '@/hooks/useDevLoginAs';
import { useNotificationsChannel } from '@/hooks/useNotificationsChannel';
import { BrandLoader } from '@/components/BrandLoader';

// Create a query client with appropriate configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Single source of truth for between-pages / route-transition loading. Swapped
// from a generic blue lucide spinner to the branded DiscloserMark with the
// onboarding "Closer ring" pulse — same effect users see on Slide 1, kept
// here for visual continuity across auth gate / route transitions / boot.
const LoadingSpinner = () => <BrandLoader />;

function App() {
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Shorter loading delay for better UX
    const timer = setTimeout(() => {
      setLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  // The actual app content with AuthProvider wrapped in QueryClientProvider
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </QueryClientProvider>
  );
}

// RootRedirect: discloser.co/ now serves the React app's seller landing
// (replacing the old Framer marketing site). Authed users still skip the
// marketing page and land directly in /listings — same one-tap entry as
// before. Unauthed visitors see the seller variant by default; /buyer and
// /broker route to the other audiences.
const RootRedirect = () => {
  const { user } = useAuth();
  if (user) return <Navigate to="/listings" replace />;
  return <LandingColorSeller />;
};

// LegacyPropertyRedirect: /admin/properties/:id → /admin/customers/:id (for old links).
const LegacyPropertyRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/admin/customers/${id ?? ''}`} replace />;
};

// ProtectedRoute: auth gate + ErrorBoundary safety net. Every authenticated
// route now surfaces an error page with retry instead of rendering blank
// when a client-side crash happens (historically we had zero coverage — the
// TDZ crash in Summary looked like "listings is blank after upload"). DO NOT
// pass a `fallback` prop to the wrapped ErrorBoundary — the default UI
// gives the user a safe generic recovery path while local/dev-preview
// diagnostics stay available off-screen for debugging.
const ProtectedRoute = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    console.log('User not authenticated, redirecting to /auth from', location.pathname);
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
};

// This component contains all the routes and is wrapped by AuthProvider
function AppContent() {
  const { user } = useAuth();
  // Monitor quota for authenticated users
  useQuota();
  // Capture referral slug from ?ref= URL param
  useReferralCapture();
  // Dev-only: ?as=<role> trades for a flows201 test-user session so
  // role-by-role dogfood doesn't require re-auth. Dead-code in prod
  // builds via the DEMO_ROLE_SWITCHER_ENABLED gate.
  useDevLoginAs();
  // Realtime notifications: subscribes to `user-events:{user_id}` while
  // authed and renders sonner toasts on invite_received / message_received.
  useNotificationsChannel(user?.id ?? null);

  return (
    <>
      <Toaster />
      <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/buyer" element={<LandingColorBuyer />} />
        <Route path="/broker" element={<LandingColorEfficiency />} />
        <Route path="/scan" element={<LandingScan />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/update-password" element={<UpdatePassword />} />
        <Route path="/docs/*" element={<Docs />} />
        <Route path="/invite/:token" element={<AgentInvite />} />
        <Route path="/l/:slug" element={<PublicListing />} />
        <Route path="/offer-comparison/:token" element={<OfferComparisonShare />} />
        <Route
          path="/listings"
          element={
            <ProtectedRoute>
              <Listings />
            </ProtectedRoute>
          }
        />
        <Route path="/upgrade" element={<Upgrade />} />
        <Route path="/preview/upgrade-lite" element={<UpgradeLite />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/subscription" element={
          <ProtectedRoute>
            <Subscription />
          </ProtectedRoute>
        } />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/settings/:section" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/new-house" element={
          <ProtectedRoute>
            <NewHouse />
          </ProtectedRoute>
        } />
        <Route path="/upload" element={<Navigate to="/new-house" replace />} />
        <Route path="/summary/:id" element={
          <ProtectedRoute>
            <Summary />
          </ProtectedRoute>
        } />
        <Route path="/summary/:id/:section" element={
          <ProtectedRoute>
            <Summary />
          </ProtectedRoute>
        } />
        <Route path="/chat" element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        } />
        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <Search />
            </ProtectedRoute>
          }
        />
        <Route path="/semantic-search" element={<Navigate to="/listings" replace />} />
        
        <Route path="/preview/b" element={<PreviewB />} />
        <Route path="/preview/c-home" element={<PreviewCHome />} />
        <Route path="/preview/c-add" element={<PreviewCAddProperty />} />
        <Route path="/preview/c-settings" element={<PreviewCSettings />} />
        <Route path="/preview/c-settings/:section" element={<PreviewCSettings />} />
        <Route path="/preview/offers-comparison" element={<OfferComparisonPreview />} />
        <Route path="/preview/offers-comparison/share" element={<OfferComparisonPreview />} />
        <Route path="/preview/c" element={<PreviewC />} />
        <Route path="/preview/c/:section" element={<PreviewC />} />

        <Route path="/preview/landing" element={<LandingIndex />} />
        <Route path="/preview/landing/seller" element={<LandingSeller />} />
        <Route path="/preview/landing/buyer" element={<LandingBuyer />} />
        <Route path="/preview/landing/efficiency" element={<LandingEfficiency />} />

        <Route path="/preview/homebuyer/firsttime" element={<HomeBuyerFirst />} />
        <Route path="/preview/homebuyer/family" element={<HomeBuyerFamily />} />
        <Route path="/preview/homebuyer/savvy" element={<HomeBuyerSavvy />} />

        <Route path="/preview/landing-color/seller" element={<LandingColorSeller />} />
        <Route path="/preview/landing-color/buyer" element={<LandingColorBuyer />} />
        <Route path="/preview/landing-color/efficiency" element={<LandingColorEfficiency />} />

        <Route path="/walkthrough" element={<Walkthrough />} />
        <Route path="/preview/walkthrough" element={<Navigate to="/walkthrough" replace />} />

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="customers" element={<AdminAllProperties />} />
          <Route path="customers/:id" element={<AdminPropertyDashboard />} />
          <Route path="properties" element={<Navigate to="/admin/customers" replace />} />
          <Route path="properties/:id" element={<LegacyPropertyRedirect />} />
          <Route
            path="pipelines"
            element={
              <AdminPlaceholder
                eyebrow="Operations"
                title="Pipelines"
                description="Cross-property feed of pipeline runs. Today this lives per-property under each customer's Pipeline tab; the cross-property view ships when the reprocess action lands."
                shipsIn="Ships with PR 2"
              />
            }
          />
          <Route
            path="closer"
            element={
              <AdminPlaceholder
                eyebrow="Operations"
                title="Closer threads"
                description="Cross-property view of every Closer chat session, with retrieved chunks and token cost. Today, jump into a customer to see their threads under the Closer tab."
              />
            }
          />
          <Route
            path="llm"
            element={
              <AdminPlaceholder
                eyebrow="Operations"
                title="LLM costs"
                description="Aggregate spend by model, function, customer. Backed by llm_call_story_by_property_user_date — wiring the chart in a follow-up."
              />
            }
          />
          <Route
            path="errors"
            element={
              <AdminPlaceholder
                eyebrow="Debug"
                title="Errors"
                description="Edge-function and client-side error feed. Sourced from Supabase function logs (and Sentry once wired)."
                shipsIn="Ships with PR 3"
              />
            }
          />
          <Route path="audit-log" element={<AdminAuditLog />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </>
  );
}

export default App;
